/**
 * UnifiedSelectOptionDialog — visibility-duration prompt shown by
 * `UnifiedSelectPopover` when the Opcja action is clicked, before any
 * `POST /reservations/batch-reserve` call is issued (CU-86carf2rw).
 *
 * `POST /reservations/batch-reserve` requires `targetVisibilityDays` (an
 * integer, 1-365) whenever `status === 'OPTION'` — see
 * `packages/contracts/src/schemas/reservation-batch.ts`. This dialog lets
 * the caller pick that value either directly ("Liczba dni") or by picking an
 * end date ("Data"), converted back to a day count via the shared
 * `inclusiveDays` helper. Both modes are kept in sync when the user switches
 * between them, and the resulting value is always clamped to 1-365
 * client-side so a confirmed submission can never fail server-side
 * validation for being out of range.
 */
import * as React from 'react';
import { addDays } from 'date-fns';
import { pl } from 'date-fns/locale';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { RadioGroup, RadioGroupItem } from '@ooh/ui/components/radio-group';
import { Input } from '@ooh/ui/components/input';
import { DatePickerButton } from '@ooh/ui/components/date-picker-button';
import { toDateStr } from '@ooh/ui/lib/date-string';
import { MAX_OPTION_VISIBILITY_DAYS } from '@ooh/contracts';
import { inclusiveDays } from '../lib/inclusive-days';

// ─── Constants / helpers ────────────────────────────────────────

const MIN_VISIBILITY_DAYS = 1;
const MAX_VISIBILITY_DAYS = MAX_OPTION_VISIBILITY_DAYS;
export const DEFAULT_VISIBILITY_DAYS = 365;

function clampVisibilityDays(days: number): number {
  return Math.min(MAX_VISIBILITY_DAYS, Math.max(MIN_VISIBILITY_DAYS, Math.round(days)));
}

/** Earliest of a non-empty list of dates; falls back to "now" for an empty list. */
function earliestDate(dates: Date[]): Date {
  if (dates.length === 0) return new Date();
  return dates.reduce((earliest, date) => (date < earliest ? date : earliest));
}

// ─── Types ────────────────────────────────────────────────────

export interface UnifiedSelectOptionDialogLabels {
  title: string;
  modeDays: string;
  modeDate: string;
  confirmBtn: string;
  cancelBtn: string;
}

export interface UnifiedSelectOptionDialogProps {
  /** dateFrom of every free cell in the current selection — seeds the date-mode default. */
  freeCellDates: Date[];
  /**
   * Pre-localized message stating how many held-by-other cells in the selection cannot be
   * taken as an option (spec: "The action SHALL say so at the confirmation step instead of
   * appearing to cover the whole selection"). `undefined` when the selection has none.
   */
  excludedSummaryText?: string;
  labels: UnifiedSelectOptionDialogLabels;
  /** True while confirming is disallowed for a reason outside this dialog's own validation (e.g. no offer chosen, mutation pending). */
  disabled?: boolean;
  onConfirm: (targetVisibilityDays: number) => void;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────

/** Caller mounts this ONLY while its own `optionDialogOpen` state is true — mount/unmount
 * (not an internal `open` prop) resets this component's mode/days/date state so a fresh
 * open always recomputes its defaults from the LATEST `freeCellDates`. */
export function UnifiedSelectOptionDialog({
  freeCellDates,
  excludedSummaryText,
  labels,
  disabled = false,
  onConfirm,
  onClose,
}: UnifiedSelectOptionDialogProps) {
  const [mode, setMode] = React.useState<'days' | 'date'>('days');
  const [days, setDays] = React.useState(DEFAULT_VISIBILITY_DAYS);
  const [daysFocused, setDaysFocused] = React.useState(false);
  const [daysRawText, setDaysRawText] = React.useState('');
  // -1: see the identical comment in switchMode's days->date branch — this default
  // must also round-trip back through inclusiveDays to exactly DEFAULT_VISIBILITY_DAYS.
  const [dateStr, setDateStr] = React.useState(() =>
    toDateStr(addDays(earliestDate(freeCellDates), DEFAULT_VISIBILITY_DAYS - 1)),
  );
  // Tracks which field the user last actually edited. Both fields start with their
  // OWN independent default (days=365; date=earliest selected cell + 365, which is
  // usually NOT the same instant as today+365) — switching modes while neither field
  // has been touched must leave the untouched field's sensible default alone instead
  // of clobbering it with a today-based recompute. Once a field is edited, THAT field
  // becomes the source of truth the other field recomputes from on every subsequent
  // mode switch.
  const [sourceOfTruth, setSourceOfTruth] = React.useState<'days' | 'date' | 'none'>('none');

  // Anchor date-mode day<->date conversions on the selection's OWN earliest cell
  // dateFrom, not "now" — the server computes the OPTION's end as
  // `option.dateFrom + targetVisibilityDays` (`calculateOptionEnd` in
  // reservations.service.ts), so the day count must be derived from that same
  // anchor or the displayed date and the submitted value silently diverge.
  const anchorDate = earliestDate(freeCellDates);
  const anchorDateStr = toDateStr(anchorDate);
  // While the days input is focused, `daysRawText` is the source of truth (not yet
  // committed to `days` — that only happens on blur). Reading it here too means
  // clicking Confirm without blurring first (e.g. typing then clicking straight to
  // the button) uses what the user actually typed, clamped, rather than silently
  // submitting the last-committed `days` value the field no longer displays.
  const liveDays = daysFocused ? Number.parseInt(daysRawText, 10) : days;
  const targetVisibilityDays =
    mode === 'days'
      ? Number.isFinite(liveDays)
        ? clampVisibilityDays(liveDays)
        : null
      : dateStr
        ? clampVisibilityDays(inclusiveDays(anchorDateStr, dateStr))
        : null;
  const canConfirm = !disabled && targetVisibilityDays !== null;

  function switchMode(nextMode: 'days' | 'date') {
    if (nextMode === mode) return;
    if (nextMode === 'date' && sourceOfTruth === 'days') {
      // -1: inclusiveDays(anchor, anchor + N) is N+1 (inclusive of both endpoints,
      // matching the server's own `dateFrom + (targetVisibilityDays - 1)` end-date
      // formula in calculateOptionEnd), so the date shown for N days must be
      // anchor + (N - 1) for the round trip back through inclusiveDays to yield N
      // again — otherwise switching days -> date -> confirm silently submits N+1.
      setDateStr(toDateStr(addDays(anchorDate, days - 1)));
    } else if (nextMode === 'days' && sourceOfTruth === 'date') {
      setDays(dateStr ? clampVisibilityDays(inclusiveDays(anchorDateStr, dateStr)) : days);
    }
    setMode(nextMode);
  }

  function handleDaysFocus() {
    setDaysRawText(String(days));
    setDaysFocused(true);
  }

  function handleDaysBlur() {
    setDaysFocused(false);
    const parsed = Number.parseInt(daysRawText, 10);
    setDays(Number.isFinite(parsed) ? clampVisibilityDays(parsed) : days);
    setSourceOfTruth('days');
  }

  function handleDateChange(next: string) {
    setDateStr(next);
    setSourceOfTruth('date');
  }

  function handleConfirm() {
    if (targetVisibilityDays === null) return;
    onConfirm(targetVisibilityDays);
  }

  return (
    <ModalDialog
      testId="unified-select-option-dialog"
      header={<span className="text-base font-semibold">{labels.title}</span>}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="unified-select-option-cancel"
            onClick={onClose}
          >
            {labels.cancelBtn}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canConfirm}
            data-testid="unified-select-option-confirm"
            onClick={handleConfirm}
          >
            {labels.confirmBtn}
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <div className="space-y-3">
        {excludedSummaryText ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="unified-select-option-excluded-summary"
          >
            {excludedSummaryText}
          </p>
        ) : null}
        <RadioGroup value={mode} onValueChange={(value) => switchMode(value as 'days' | 'date')}>
          <RadioGroupItem
            value="days"
            label={labels.modeDays}
            data-testid="unified-select-option-mode-days"
          />
          <RadioGroupItem
            value="date"
            label={labels.modeDate}
            data-testid="unified-select-option-mode-date"
          />
        </RadioGroup>
        {mode === 'days' ? (
          <Input
            type="text"
            inputMode="numeric"
            value={daysFocused ? daysRawText : String(days)}
            onFocus={handleDaysFocus}
            onBlur={handleDaysBlur}
            onChange={(e) => setDaysRawText(e.target.value)}
            aria-label={labels.modeDays}
            data-testid="unified-select-option-days-input"
          />
        ) : (
          <DatePickerButton
            value={dateStr}
            onChange={handleDateChange}
            locale={pl}
            aria-label={labels.modeDate}
            testId="unified-select-option-date-input"
          />
        )}
      </div>
    </ModalDialog>
  );
}
